import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styleNav from "./Navbar.module.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  faBars,
  faHouse,
  faMagnifyingGlass,
  faRightFromBracket,
  faUnlockKeyhole,
} from "@fortawesome/free-solid-svg-icons";
import { getUserRoleLabel } from "../../utils/permissions";

const PAGE_META = {
  1: { title: "Dashboard", subtitle: "Overview of your CMS workspace." },
  2: { title: "Scenes", subtitle: "Manage immersive scenes and deployments." },
  3: { title: "Interactions", subtitle: "Configure interactive elements." },
  4: { title: "Categories", subtitle: "Organize content categories." },
  5: { title: "Category Filters", subtitle: "Refine how categories are filtered." },
  6: { title: "Product Categories", subtitle: "Manage product groupings." },
  7: { title: "Unity Scene", subtitle: "Unity WebGL scene assets." },
  8: { title: "Products", subtitle: "Product catalog and details." },
  9: { title: "Users", subtitle: "Team access and roles." },
  10: { title: "Audit", subtitle: "Activity and change history." },
  11: { title: "File Library", subtitle: "Uploaded media and assets." },
  12: { title: "Basic Settings", subtitle: "Core platform configuration." },
  13: { title: "Homepage Options", subtitle: "Landing page behavior and content." },
  14: { title: "Theme Options", subtitle: "Branding and visual theme." },
  15: { title: "Share Icons", subtitle: "Social sharing configuration." },
  17: { title: "Filters", subtitle: "Search and filter settings." },
  18: { title: "360 Tours", subtitle: "Panorama tours and hotspots." },
  19: { title: "Web URLs", subtitle: "Manage external websites and web experiences." },
};

const Navbar = ({ setVisibility, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showDropDown, setShowDropdown] = useState(false);

  const determineNavBarTitle = () => {
    const path = location?.pathname;
    if (path === "/web-twinprocms/dashboard" || path === "/web-twinprocms") return 1;
    if (path.includes("/web-twinprocms/scene")) return 2;
    if (path.includes("/web-twinprocms/interaction")) return 3;
    if (path === "/web-twinprocms/categories") return 4;
    if (path === "/web-twinprocms/category-filters") return 5;
    if (path.includes("/web-twinprocms/product-category")) return 6;
    if (path.includes("/web-twinprocms/unity-scene")) return 7;
    if (path.includes("/web-twinprocms/products")) return 8;
    if (path.includes("/web-twinprocms/users")) return 9;
    if (path.includes("/web-twinprocms/audit")) return 10;
    if (path.includes("/web-twinprocms/file-library")) return 11;
    if (path === "/web-twinprocms/basic-settings") return 12;
    if (path === "/web-twinprocms/homepage-options") return 13;
    if (path === "/web-twinprocms/theme-options") return 14;
    if (path === "/web-twinprocms/share-icon") return 15;
    if (path === "/web-twinprocms/filters") return 17;
    if (path.includes("/web-twinprocms/360-tours")) return 18;
    if (path.includes("/web-twinprocms/web-urls")) return 19;
    return 1;
  };

  const pageKey = determineNavBarTitle();
  const pageMeta = PAGE_META[pageKey] || PAGE_META[1];
  const displayName = user?.email?.split("@")[0] || "User";
  const roleLabel = getUserRoleLabel(user);

  const handleLogout = () => {
    localStorage.removeItem("Token");
    navigate("/login");
  };

  const handleChangePassword = () => {
    setShowDropdown(false);
    navigate("/forgot-password");
  };

  return (
    <header className={styleNav.dashboardHeader}>
      <div className={styleNav.headerLeft}>
        <button
          type="button"
          className={styleNav.hamburgerMenu}
          onClick={() => setVisibility(prev => !prev)}
          aria-label="Toggle navigation"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
        <div className={styleNav.headerTitles}>
          <h2>
            {pageKey === 1 ? `Welcome back, ${displayName}` : pageMeta.title}
          </h2>
          <p>{pageMeta.subtitle}</p>
        </div>
      </div>

      <div className={styleNav.headerActions}>
        <div className={styleNav.searchBar}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input type="text" placeholder="Search CMS..." aria-label="Search CMS" />
        </div>

        <Link to="/" className={styleNav.homeLink}>
          <FontAwesomeIcon icon={faHouse} />
          <span style={{ marginLeft: "0.45rem" }}>Home</span>
        </Link>

        <div className={styleNav.userMenu}>
          <button
            type="button"
            className={styleNav.userTrigger}
            onClick={() => setShowDropdown(prev => !prev)}
            aria-expanded={showDropDown}
          >
            <img
              className={styleNav.userAvatar}
              src={`${window.location.origin}/assets/${process.env.REACT_APP_LOGO}`}
              alt="User menu"
            />
          </button>

          {showDropDown && (
            <div className={styleNav.userDropdown}>
              <span className={styleNav.userEmail}>{user?.email}</span>
              {roleLabel ? (
                <span className={styleNav.userRole}>{roleLabel}</span>
              ) : null}
              <hr className={styleNav.dropdownDivider} />
              <span
                className={styleNav.dropdownAction}
                onClick={handleChangePassword}
              >
                <FontAwesomeIcon icon={faUnlockKeyhole} /> Change Password
              </span>
              <span className={styleNav.dropdownAction} onClick={handleLogout}>
                <FontAwesomeIcon icon={faRightFromBracket} /> Logout
              </span>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
